import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import contact from '../services/ContactService';
import { contact_slice_name, contact_thunks } from '../constants/contact';
import {
  getContactIndexFromApplicantId,
  getContactIndexFromMeta,
  getContactIndexFromPayload,
} from './ContactSelectors';
import { deleteApplicantById } from './ApplicationSlice';
import { getEmailValidationErrorMessage } from '../utilities/application/contactHelper';
import { contact_email_validation } from '../constants/contact';

const status = {
  applicantId: null,
  loading: false,
  isSatisfied: false,
  confirmEmail: '',
  error: false,
  emailError: false,
  emailConfirmError: false,
  postalAddresses: [],
  address: null,
  emailValid: false,
  emailMatch: false,
  phoneNumberValid: false,
};

export const initialData = {
  loading: false,
  error: false,
  pristine: true,
  contacts: [{ ...status }, { ...status }],
};

export const fetchAddressLookupByPostcode = createAsyncThunk(
  contact_thunks.CONTACT_FETCH_ADDRESS_LOOKUP_BY_POSTCODE,
  async ({ postcode }, { rejectWithValue }) => {
    try {
      const response = await contact.fetchAddressLookupByPostcode(postcode);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchAddressLookupById = createAsyncThunk(
  contact_thunks.CONTACT_FETCH_ADDRESS_LOOKUP_BY_ID,
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await contact.fetchAddressLookupById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchValidateEmail = createAsyncThunk(
  contact_thunks.CONTACT_FETCH_VALIDATE_EMAIL,
  async ({ email }, { rejectWithValue }) => {
    try {
      if (email === '') {
        throw new Error('Please enter an email address');
      }
      const response = await contact.fetchValidateEmail(email);
      if (!response.data.data || !contact_email_validation.test(email)) {
        throw new Error('Invalid email');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const contactSlice = createSlice({
  name: contact_slice_name,
  initialState: initialData,
  reducers: {
    reset() {
      return initialData;
    },
    resetAddress(state) {
      state.contacts[0].address = null;
      state.contacts[1].address = null;
    },
    setApplicant1Id(state, action) {
      state.contacts[0].applicantId = action.payload;
    },
    setApplicant2Id(state, action) {
      state.contacts[1].applicantId = action.payload;
    },
    setApplicantStatusEmailValid(state, action) {
      const index = getContactIndexFromPayload(state, action);
      state.contacts[index].emailValid = action.payload.emailValid;
    },
    setApplicantStatusEmailMatch(state, action) {
      const index = getContactIndexFromPayload(state, action);
      state.contacts[index].emailMatch = action.payload.emailMatch;
    },
    setApplicantStatusEmailConfirmError(state, action) {
      const index = getContactIndexFromPayload(state, action);
      state.contacts[index].emailConfirmError = action.payload.error;
    },
    setApplicantStatusPhoneNumberValid(state, action) {
      const index = getContactIndexFromPayload(state, action);
      state.contacts[index].phoneNumberValid = action.payload.phoneNumberValid;
    },
  },
  extraReducers: {
    [fetchAddressLookupByPostcode.pending]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].loading = true;
    },
    [fetchAddressLookupByPostcode.fulfilled]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].postalAddresses = action.payload.data
        ? action.payload.data.results
        : [];
      state.contacts[index].loading = false;
    },
    [fetchAddressLookupByPostcode.rejected]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].error = action.payload.errors;
      state.contacts[index].loading = false;
    },
    [fetchAddressLookupById.pending]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].loading = true;
    },
    [fetchAddressLookupById.fulfilled]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      const { data } = action.payload;
      state.contacts[index].postalAddresses = [];
      state.contacts[index].address = {
        ...data,
        cityName: data.town,
      };
      state.contacts[index].loading = false;
    },
    [fetchAddressLookupById.rejected]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].error = action.payload.errors;
      state.contacts[index].loading = false;
    },
    [fetchValidateEmail.pending]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].loading = true;
      state.contacts[index].emailErrorMessage = null;
    },
    [fetchValidateEmail.fulfilled]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      if (action.payload?.data?.isValid === true) {
        state.contacts[index].emailError = false;
        state.contacts[index].emailValid = true;
      } else {
        state.contacts[index].emailValid = false;
        state.contacts[index].emailError = getEmailValidationErrorMessage(
          action.payload?.data?.status
        );
      }
      state.contacts[index].loading = false;
    },
    [fetchValidateEmail.rejected]: (state, action) => {
      const index = getContactIndexFromMeta(state, action);
      state.contacts[index].emailValid = false;
      state.contacts[index].emailError = action.payload;
      state.contacts[index].loading = false;
    },
    [deleteApplicantById.fulfilled]: (state, action) => {
      const index = getContactIndexFromApplicantId(state, action.meta.arg);
      if (index === 0) {
        state.contacts[0] = state.contacts[1];
        state.contacts[1] = status;
      } else if (index === 1) {
        state.contacts[1] = status;
      }
    },
  },
});

export const {
  reset,
  resetAddress,
  setApplicant1Id,
  setApplicant2Id,
  setSameAddress,
  setApplicantStatusEmailValid,
  setApplicantStatusEmailMatch,
  setApplicantStatusEmailConfirmError,
  setApplicantStatusPhoneNumberValid,
  setContactsEmailMatch,
  resetApplicantAddress,
} = contactSlice.actions;

export default contactSlice;
